import React, { useEffect, useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { CheckCircle, Calendar, CreditCard, Users, ArrowRight, Loader2 } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { Button, Card } from '../../components/ui'
import { useRegistrationStore } from '../../stores/registrationStore'
import { ProgressStepper } from '../../components/onboarding/ProgressStepper'
import { AnalyticsEvents } from '../../types/analytics'

// Use AUTH_API_URL for onboarding/subscription endpoints
const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1'
const POLLING_INTERVAL = 3000 // 3 seconds
const MAX_POLLING_ATTEMPTS = 20 // 60 seconds total

// Environment configuration for analytics
const ENV = import.meta.env.VITE_ENV
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const isAnalyticsEnabled = ENV === 'production' && !!POSTHOG_KEY

interface Subscription {
  id: string;
  status: string;
  tierId: number;
  tierName: string;
  trialEndsAt: string;
  currentPeriodEnd: string;
}

const TrialStartedPage: React.FC = () => {
  const posthog = usePostHog()
  const { registrationData, clearRegistrationData } = useRegistrationStore()
  const [subscriptionLoading, setSubscriptionLoading] = useState(true)
  const [subscriptionData, setSubscriptionData] = useState<Subscription | null>(null)
  const [pollingError, setPollingError] = useState<string | null>(null)
  const hasTrackedCheckoutComplete = useRef(false)

  useEffect(() => {
    // Redirect if no registration data
    if (!registrationData) {
      window.location.href = '/pricing';
      return;
    }
  }, [registrationData]);

  useEffect(() => {
    let attempts = 0;
    let pollInterval: NodeJS.Timeout;

    const pollSubscription = async () => {
      try {
        const response = await fetch(`${AUTH_API_URL}/subscription`, {
          method: 'GET',
          credentials: 'include',
        });

        if (!response.ok) {
          if (response.status === 404) {
            // Subscription not created yet, continue polling
            attempts++;
            if (attempts >= MAX_POLLING_ATTEMPTS) {
              setPollingError('Subscription confirmation timeout. Please check your email or contact support.');
              setSubscriptionLoading(false);
              clearInterval(pollInterval);
            }
            return;
          }
          throw new Error('Failed to fetch subscription');
        }

        const data = await response.json();

        if (data.success && data.data?.subscription) {
          const sub = data.data.subscription;

          // Check if subscription is active or trialing
          if (sub.status === 'active' || sub.status === 'trialing') {
            setSubscriptionData(sub);
            setSubscriptionLoading(false);
            clearInterval(pollInterval);

            // Track checkout completed (only once)
            if (!hasTrackedCheckoutComplete.current && isAnalyticsEnabled && posthog) {
              hasTrackedCheckoutComplete.current = true
              posthog.capture(AnalyticsEvents.SUBSCRIPTION_CHECKOUT_COMPLETED, {
                tierId: sub.tierId,
                tierName: sub.tierName,
                billingCycle: registrationData?.billingCycle || 'monthly',
              })
            }
          } else {
            // Subscription exists but not active yet
            attempts++;
            if (attempts >= MAX_POLLING_ATTEMPTS) {
              setPollingError('Subscription is pending. Please check your email.');
              setSubscriptionLoading(false);
              clearInterval(pollInterval);
            }
          }
        }
      } catch (error: any) {
        console.error('Error polling subscription:', error);
        attempts++;
        if (attempts >= MAX_POLLING_ATTEMPTS) {
          setPollingError('Unable to confirm subscription. Please contact support.');
          setSubscriptionLoading(false);
          clearInterval(pollInterval);
        }
      }
    };

    // Start polling
    pollSubscription(); // Initial call
    pollInterval = setInterval(pollSubscription, POLLING_INTERVAL);

    // Cleanup
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, []);

  const getTierName = () => {
    if (subscriptionData) {
      return subscriptionData.tierName;
    }
    // Fallback to registration data
    switch (registrationData?.selectedTierId) {
      case 1:
        return 'Starter';
      case 2:
        return 'Growth';
      case 3:
        return 'Professional';
      default:
        return 'Unknown';
    }
  };

  const getTrialDays = () => {
    if (subscriptionData?.trialEndsAt) {
      const now = new Date();
      const trialEnd = new Date(subscriptionData.trialEndsAt);
      const diffTime = trialEnd.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    }
    // Fallback to registration data
    return registrationData?.selectedTierId === 3 ? 30 : 14;
  };

  const getTrialEndDate = () => {
    if (subscriptionData?.trialEndsAt) {
      return new Date(subscriptionData.trialEndsAt);
    }
    // Fallback to calculated date
    const fallbackDate = new Date();
    fallbackDate.setDate(fallbackDate.getDate() + (registrationData?.selectedTierId === 3 ? 30 : 14));
    return fallbackDate;
  };

  const trialEndDate = getTrialEndDate();

  const handleGetStarted = () => {
    // Navigate to homepage first, then clear registration data
    // Clearing is done after setting location to avoid the useEffect redirect
    window.location.href = '/';
    // Clear registration data after navigation is initiated
    clearRegistrationData();
  };

  const steps = [
    { label: 'Account', description: 'Create account' },
    { label: 'Verify Email', description: 'Confirm email' },
    { label: 'Payment', description: 'Enter payment' },
    { label: 'Complete', description: 'Start trial' },
  ];

  // Show loading state while polling subscription
  if (subscriptionLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="container mx-auto px-4 py-6 sm:py-8">
          <ProgressStepper steps={steps} currentStep={3} />
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <Loader2 className="w-10 h-10 text-avi-primary animate-spin mb-3" />
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
              Confirming your subscription...
            </h2>
            <p className="text-sm text-gray-600">This will only take a moment</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if polling failed
  if (pollingError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="container mx-auto px-4 py-6 sm:py-8">
          <ProgressStepper steps={steps} currentStep={3} />
          <div className="flex flex-col items-center justify-center min-h-[60vh]">
            <Card className="p-5 sm:p-6 max-w-sm">
              <div className="text-center">
                <div className="text-yellow-500 text-3xl sm:text-4xl mb-3">⚠️</div>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                  Subscription Pending
                </h2>
                <p className="text-sm text-gray-600 mb-5">{pollingError}</p>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => window.location.href = '/'}
                  className="w-full text-sm"
                >
                  Go to Dashboard
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="container mx-auto px-4 py-6 sm:py-8">
        <ProgressStepper steps={steps} currentStep={3} />

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto"
        >
          <Card className="p-5 sm:p-6">
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="flex justify-center mb-5"
            >
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
              </div>
            </motion.div>

            {/* Title */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-center mb-5"
            >
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
                Welcome to Athletiverse!
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                Your {getTrialDays()}-day free trial has started
              </p>
            </motion.div>

            {/* Trial Info Card */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-r from-avi-primary to-blue-600 rounded-xl p-4 sm:p-5 mb-5 text-white"
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="flex justify-center mb-1">
                    <Calendar className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <p className="text-xs opacity-90 mb-0.5">Trial ends on</p>
                  <p className="font-bold text-sm sm:text-base">
                    {trialEndDate.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </p>
                </div>

                <div className="text-center">
                  <div className="flex justify-center mb-1">
                    <Users className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <p className="text-xs opacity-90 mb-0.5">Your plan</p>
                  <p className="font-bold text-sm sm:text-base">{getTierName()}</p>
                </div>

                <div className="text-center">
                  <div className="flex justify-center mb-1">
                    <CreditCard className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                  <p className="text-xs opacity-90 mb-0.5">First charge</p>
                  <p className="font-bold text-sm sm:text-base">{trialEndDate.toLocaleDateString()}</p>
                </div>
              </div>
            </motion.div>

            {/* What's Next */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="mb-5"
            >
              <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-3 text-center">
                What's Next?
              </h2>

              <div className="space-y-2">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-avi-primary text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-0.5">
                      Log in to your account
                    </h3>
                    <p className="text-xs text-gray-600">
                      Use your email ({registrationData?.email}) and password to access your dashboard
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-avi-primary text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-0.5">
                      Connect your social media accounts
                    </h3>
                    <p className="text-xs text-gray-600">
                      Link your social profiles to start tracking analytics
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-avi-primary text-white rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-0.5">
                      Explore your analytics dashboard
                    </h3>
                    <p className="text-xs text-gray-600">
                      Discover insights about your social media performance
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Important Notes */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-5"
            >
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Important to Know:</h3>
              <ul className="space-y-1.5 text-xs text-gray-700">
                <li className="flex items-start gap-1.5">
                  <span className="text-avi-primary mt-0.5">•</span>
                  <span>
                    You have full access to all {getTierName()} plan features during your trial
                  </span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-avi-primary mt-0.5">•</span>
                  <span>
                    No charges will be made to your card until your trial ends on{' '}
                    {trialEndDate.toLocaleDateString()}
                  </span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-avi-primary mt-0.5">•</span>
                  <span>You can cancel anytime before the trial ends with no charges</span>
                </li>
                <li className="flex items-start gap-1.5">
                  <span className="text-avi-primary mt-0.5">•</span>
                  <span>
                    Manage your subscription and billing from your account settings
                  </span>
                </li>
              </ul>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
            >
              <Button
                variant="primary"
                size="md"
                className="w-full text-sm"
                onClick={handleGetStarted}
              >
                Get Started
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </motion.div>

            {/* Support */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="mt-4 text-center text-xs text-gray-600"
            >
              Need help getting started?{' '}
              <a href="/support" className="text-avi-primary hover:underline font-medium">
                Contact our support team
              </a>
            </motion.div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default TrialStartedPage;
